import { useCallback, useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import BugReportIcon from "@mui/icons-material/BugReport";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StorageIcon from "@mui/icons-material/Storage";
import MemoryIcon from "@mui/icons-material/Memory";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import urls from "../../constants/apiurl";
import ApiCaller from "../../constants/ApiCaller";
import { showDangerToast } from "./toaster";

const AUTO_REFRESH_MS = 15000;

function ttlLabel(ttl) {
  if (ttl == null) return "—";
  if (ttl === -2) return "Key missing (−2)";
  if (ttl === -1) return "No expiry (−1)";
  if (ttl === 0) return "Expires immediately (0)";
  if (ttl > 0) return `${ttl}s remaining`;
  return String(ttl);
}

function boolChip(value, trueLabel, falseLabel, naLabel = "n/a") {
  if (value === true) return { label: trueLabel, color: "success" };
  if (value === false) return { label: falseLabel, color: "default" };
  return { label: naLabel, color: "default" };
}

function MetricCard({ title, icon, children, accent }) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderTop: accent ? `3px solid ${accent}` : undefined,
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
          {icon}
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

function Kv({ label, value, mono }) {
  return (
    <TableRow>
      <TableCell
        sx={{
          py: 0.75,
          pl: 0,
          border: 0,
          color: "text.secondary",
          width: "42%",
          verticalAlign: "top",
        }}
      >
        {label}
      </TableCell>
      <TableCell
        sx={{
          py: 0.75,
          pr: 0,
          border: 0,
          fontWeight: 600,
          fontFamily: mono ? "ui-monospace, Menlo, monospace" : "inherit",
          fontSize: mono ? 12 : "inherit",
          wordBreak: "break-all",
        }}
      >
        {value == null || value === "" ? "—" : String(value)}
      </TableCell>
    </TableRow>
  );
}

function adminTakeaway(data) {
  const seen = data?.redis?.seen;
  if (!seen) return "No Redis SEEN data yet — click Refresh.";
  if (seen.available === false) {
    return `Redis unavailable: ${seen.unavailableReason || "cannot read SEEN. Do not treat as ‘not seen’."}`;
  }
  if (seen.keyMissing) {
    return "SEEN key is missing — feed cannot exclude already-seen videos from Redis for this user.";
  }
  if ((seen.memberCount ?? 0) === 0) {
    return "SEEN key exists but is empty — nothing to exclude from Redis.";
  }
  return `SEEN has ${seen.memberCount} videos (version ${seen.version || "—"}, ${ttlLabel(seen.ttl)}).`;
}

/**
 * Dashboard UI for live user feed/Redis diagnostics.
 * Admin-service → user-service GET /admin/users/{userId}/diagnostics
 */
export function UserFeedDiagnostics({ userId, accessToken, enabled }) {
  const [videoId, setVideoId] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const videoIdRef = useRef(videoId);
  videoIdRef.current = videoId;

  const load = useCallback(
    async (opts = {}) => {
      if (!userId) return;
      const withVideo = opts.withVideo !== false;
      setLoading(true);
      setError(null);
      try {
        const trimmedVideo = withVideo ? (videoIdRef.current || "").trim() : "";
        const path = `${urls.userDiagnostics}/${userId}/diagnostics`;
        const params = trimmedVideo ? { videoId: trimmedVideo } : {};
        const res = await ApiCaller.get(params, path, accessToken);
        const response = await res.response.json();

        if (res.status === 400) {
          setError(response?.error || response?.message || "Invalid request");
          return;
        }

        if (response?.status == 200 || response?.success || res.status === 200) {
          setData(response?.data ?? response);
          setLastUpdated(new Date());
        } else {
          const msg = response?.message || "Failed to load diagnostics";
          setError(msg);
          showDangerToast(msg);
        }
      } catch (e) {
        console.error("UserFeedDiagnostics load failed", e);
        setError(e?.message || "Failed to load diagnostics");
        showDangerToast(e?.message || "Failed to load diagnostics");
      } finally {
        setLoading(false);
      }
    },
    [userId, accessToken]
  );

  useEffect(() => {
    if (!enabled || !userId) return;
    load();
  }, [enabled, userId, load]);

  // Auto-refresh overview (and explain if videoId is filled). Off by default.
  useEffect(() => {
    if (!enabled || !userId || !autoRefresh) return undefined;
    const id = setInterval(() => load(), AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [enabled, userId, autoRefresh, load]);

  const seen = data?.redis?.seen;
  const feedVersion = data?.redis?.feedVersion;
  const feedState = data?.redis?.feedState;
  const database = data?.database;
  const explain = data?.explainVideo;
  const meta = data?.meta;

  return (
    <Box sx={{ mb: 3 }}>
      {/* Toolbar */}
      <Card sx={{ p: { xs: 2, sm: 2.5 }, mb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <BugReportIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                Feed / Redis Diagnostics Dashboard
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Live snapshot for this user
                {lastUpdated
                  ? ` · Updated ${lastUpdated.toLocaleTimeString()}`
                  : ""}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  Auto-refresh ({AUTO_REFRESH_MS / 1000}s)
                </Typography>
              }
            />
            <Button
              variant="contained"
              size="small"
              startIcon={
                loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
              }
              onClick={() => load()}
              disabled={loading || !userId}
              sx={{ textTransform: "none" }}
            >
              {loading ? "Refreshing…" : "Refresh now"}
            </Button>
          </Stack>
        </Stack>

        <Alert severity="info" sx={{ mt: 2 }}>
          {adminTakeaway(data)}
        </Alert>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mt={2}>
          <TextField
            size="small"
            label="Video ID (Explain why this video can appear)"
            value={videoId}
            onChange={(e) => setVideoId(e.target.value)}
            fullWidth
            placeholder="Paste video UUID"
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => load({ withVideo: true })}
            disabled={loading || !userId}
            sx={{ textTransform: "none", whiteSpace: "nowrap", minWidth: 140 }}
          >
            Explain video
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Tip: Explain video loads pool membership (heavier). Auto-refresh also re-runs explain
          if a video ID is filled — leave video ID empty for lighter Redis/DB-only refresh.
        </Typography>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!data && loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {data && (
        <>
          {/* Status strip */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
            <Chip
              size="small"
              label={
                seen?.available === false
                  ? "Redis: DOWN"
                  : "Redis: OK"
              }
              color={seen?.available === false ? "error" : "success"}
            />
            <Chip
              size="small"
              label={
                seen?.keyMissing
                  ? "SEEN: missing"
                  : `SEEN: ${seen?.memberCount ?? 0} members`
              }
              color={seen?.keyMissing ? "warning" : "primary"}
            />
            <Chip
              size="small"
              label={
                database?.available === false
                  ? "Database: DOWN"
                  : "Database: OK"
              }
              color={database?.available === false ? "error" : "success"}
              variant="outlined"
            />
            {meta?.userId && (
              <Chip size="small" label={`User: ${meta.userId}`} variant="outlined" />
            )}
          </Stack>

          <Grid container spacing={2}>
            {/* SEEN */}
            <Grid item xs={12} md={6}>
              <MetricCard
                title="Redis SEEN"
                icon={<MemoryIcon color="primary" fontSize="small" />}
                accent="#5e72e4"
              >
                <Typography variant="h3" fontWeight={800} lineHeight={1.1} mb={1}>
                  {seen?.available === false
                    ? "—"
                    : seen?.keyMissing
                      ? "Missing"
                      : seen?.memberCount ?? 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={1.5}>
                  {seen?.available === false
                    ? "Cannot read SEEN"
                    : seen?.keyMissing
                      ? "No SEEN key for this user"
                      : "videos in SEEN zset"}
                </Typography>
                <Table size="small">
                  <TableBody>
                    <Kv label="Available" value={seen?.available === false ? "No" : "Yes"} />
                    <Kv label="Key missing" value={seen?.keyMissing ? "Yes" : "No"} />
                    <Kv label="TTL" value={ttlLabel(seen?.ttl)} />
                    <Kv label="Version" value={seen?.version} mono />
                    <Kv label="Oldest video" value={seen?.oldestMember} mono />
                    <Kv label="Newest video" value={seen?.newestMember} mono />
                    <Kv
                      label="History span"
                      value={
                        seen?.historySpan != null
                          ? `${Math.round(seen.historySpan)}s`
                          : null
                      }
                    />
                    <Kv label="Write limit" value={seen?.configuredLimit} />
                    <Kv label="Atomic limit" value={seen?.atomicWriteLimit} />
                    <Kv label="Lookup window" value={seen?.lookupWindow} />
                  </TableBody>
                </Table>
                {seen?.unavailableReason && (
                  <Alert severity="error" sx={{ mt: 1.5 }}>
                    {seen.unavailableReason}
                  </Alert>
                )}
              </MetricCard>
            </Grid>

            {/* Feed version + state */}
            <Grid item xs={12} md={6}>
              <Stack spacing={2} height="100%">
                <MetricCard
                  title="Feed version cache"
                  icon={<StorageIcon color="action" fontSize="small" />}
                  accent="#11cdef"
                >
                  <Stack direction="row" spacing={1} mb={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={feedVersion?.available === false ? "Redis error" : "Redis OK"}
                      color={feedVersion?.available === false ? "error" : "success"}
                    />
                    <Chip
                      size="small"
                      label={feedVersion?.present ? "Present" : "Not present"}
                      color={feedVersion?.present ? "primary" : "default"}
                    />
                  </Stack>
                  <Table size="small">
                    <TableBody>
                      <Kv label="Value" value={feedVersion?.value} mono />
                      <Kv label="TTL" value={ttlLabel(feedVersion?.ttl)} />
                    </TableBody>
                  </Table>
                </MetricCard>

                <MetricCard
                  title="Feed state cache"
                  icon={<StorageIcon color="action" fontSize="small" />}
                  accent="#2dce89"
                >
                  <Stack direction="row" spacing={1} mb={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={feedState?.available === false ? "Redis error" : "Redis OK"}
                      color={feedState?.available === false ? "error" : "success"}
                    />
                    <Chip
                      size="small"
                      label={feedState?.present ? "Present" : "Not present"}
                      color={feedState?.present ? "primary" : "default"}
                    />
                  </Stack>
                  {feedState?.present ? (
                    <Table size="small">
                      <TableBody>
                        <Kv label="TTL" value={ttlLabel(feedState?.ttl)} />
                        <Kv label="Liked videos" value={feedState?.likedVideoCount} />
                        <Kv label="Saved videos" value={feedState?.savedVideoCount} />
                        <Kv label="Following" value={feedState?.followingOwnerCount} />
                        <Kv label="Blocked owners" value={feedState?.blockedOwnerCount} />
                        <Kv label="Subscriptions" value={feedState?.subscriptionOwnerCount} />
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No cached liked / blocked / following state for this user right now.
                    </Typography>
                  )}
                </MetricCard>
              </Stack>
            </Grid>

            {/* Database */}
            <Grid item xs={12}>
              <MetricCard
                title="Database (history / likes)"
                icon={<StorageIcon color="secondary" fontSize="small" />}
                accent="#fb6340"
              >
                {database?.available === false ? (
                  <Alert severity="error">{database.unavailableReason}</Alert>
                ) : (
                  <Grid container spacing={2}>
                    {[
                      ["View history", database?.viewHistory],
                      ["Instant history", database?.instantHistory],
                      ["Watch history", database?.watchHistory],
                      ["Likes", database?.likes],
                    ].map(([name, row]) => (
                      <Grid item xs={12} sm={6} md={3} key={name}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: "grey.50",
                            border: "1px solid",
                            borderColor: "grey.200",
                            height: "100%",
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight={700} mb={1}>
                            {name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Count:{" "}
                            {row?.countAvailable
                              ? row.count
                              : row?.countUnavailableReason
                                ? "n/a"
                                : "—"}
                          </Typography>
                          {row?.presentForVideo != null && (
                            <Chip
                              size="small"
                              sx={{ mt: 1 }}
                              label={
                                row.presentForVideo
                                  ? "Present for video"
                                  : "Absent for video"
                              }
                              color={row.presentForVideo ? "success" : "default"}
                            />
                          )}
                          {!row?.countAvailable && row?.countUnavailableReason && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mt={1}
                            >
                              {row.countUnavailableReason}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </MetricCard>
            </Grid>

            {/* Explain video */}
            <Grid item xs={12}>
              <MetricCard
                title="Explain video"
                icon={<VideoLibraryIcon color="primary" fontSize="small" />}
                accent="#f5365c"
              >
                {!explain ? (
                  <Typography variant="body2" color="text.secondary">
                    Paste a video UUID above and click <strong>Explain video</strong> to see
                    why that video can appear in FOR_YOU for this user (SEEN, DB, pools).
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" mb={1} fontFamily="monospace">
                      videoId: {explain.videoId}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
                      {(() => {
                        const a = boolChip(
                          explain.wouldExcludeBySeenNow,
                          "Would EXCLUDE by SEEN now",
                          "Would NOT exclude by SEEN now"
                        );
                        return <Chip size="small" label={a.label} color={a.color} />;
                      })()}
                      {(() => {
                        const a = boolChip(
                          explain.wouldSurviveSeenFilterNow,
                          "Would SURVIVE SEEN filter now",
                          "Would NOT survive SEEN filter"
                        );
                        return <Chip size="small" label={a.label} color={a.color} />;
                      })()}
                    </Stack>

                    <Typography variant="subtitle2" fontWeight={700} mb={1}>
                      Reason codes (admin-readable)
                    </Typography>
                    <Box
                      sx={{
                        maxHeight: 280,
                        overflow: "auto",
                        border: "1px solid",
                        borderColor: "grey.200",
                        borderRadius: 1,
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableBody>
                          {(explain.reasons || []).map((r, i) => (
                            <TableRow key={`${r.code}-${i}`} hover>
                              <TableCell sx={{ width: 110, fontWeight: 600 }}>
                                {r.category}
                              </TableCell>
                              <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                {r.code}
                                {r.inferred ? " · inferred" : ""}
                              </TableCell>
                              <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                                {r.detail || "—"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </>
                )}
              </MetricCard>
            </Grid>
          </Grid>

          {/* Raw JSON — secondary */}
          <Accordion sx={{ mt: 2 }} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight={700}>
                Raw JSON (for engineers)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  maxHeight: 360,
                  overflow: "auto",
                  bgcolor: "grey.100",
                  borderRadius: 1,
                  fontSize: 11,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                {JSON.stringify(data, null, 2)}
              </Box>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Box>
  );
}
